/**
 * Central EventBus with hierarchical topics and pluggable transports
 * 
 * This is the "nervous system" of Pivot.is. All modules communicate via events,
 * never direct imports. The bus supports:
 * - Hierarchical topic matching (e.g., "a.b.*" matches "a.b.c")
 * - Middleware for payload transformation
 * - Interceptors for event filtering/cancellation
 * - Pluggable transports for persistence and network sync
 * 
 * Example topics:
 * - "ui.square.update"
 * - "state.user"
 * - "command.undo"
 */

import type { Transport } from './transport.js';
import type { PivotEventMap } from '../types/events.js';
import { log } from '../services/logging/index.js';

// Handler type - accepts any payload for flexibility
type Handler = (payload: unknown) => void;

/**
 * Central Pub/Sub EventBus with hierarchical topics and pluggable transports.
 * 
 * Supports hierarchical event matching:
 * - "a.b.c" → matches "a.b.c", "a.b.*", "a.*"
 * 
 * Transports can mirror events to persistence/network layers.
 */
export class EventBus<Events extends Record<string, unknown> = PivotEventMap> {
  private listeners = new Map<keyof Events | string, Set<Handler>>();
  private transports: Transport[] = [];
  private middlewares: Array<(event: string, payload: unknown) => unknown> = [];
  private interceptors: Array<(event: string, payload: unknown) => boolean | void> = [];
  private emitDepth = 0;
  private maxEmitDepth = 50; // Prevent infinite recursion

  /**
   * Subscribe to an event with a handler.
   * Supports wildcard matching: "a.b.*" will match "a.b.c", "a.b.d", etc.
   */
  on<K extends keyof Events & string>(
    event: K | `${K}.*`,
    handler: (payload: Events[K]) => void
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    // Cast handler to internal Handler type for storage
    this.listeners.get(event)!.add(handler as Handler);
  }

  /**
   * Unsubscribe from an event.
   */
  off<K extends keyof Events & string>(
    event: K | `${K}.*`,
    handler: (payload: Events[K]) => void
  ): void {
    this.listeners.get(event)?.delete(handler as Handler);
  }

  /**
   * Subscribe to an event once (auto-unsubscribes after first emission).
   */
  once<K extends keyof Events & string>(
    event: K,
    handler: (payload: Events[K]) => void
  ): void {
    const wrap = (p: Events[K]) => {
      handler(p);
      this.off(event, wrap);
    };
    this.on(event, wrap);
  }

  /**
   * Register a transport for event mirroring.
   * The transport can inject remote events via its onMessage callback.
   */
  useTransport(t: Transport): void {
    this.transports.push(t);
    // Allow transport to push remote messages back in:
    // Mark state.update events as remote so state.ts can handle them properly
    t.onMessage = (event, payload) => {
      if (event === 'state.update' && typeof payload === 'object' && payload !== null) {
        // Add source marker for remote events
        (payload as { source?: string }).source = 'remote';
      }
      this.emitRaw(event, payload, { localOnly: true });
    };
    t.connect();
  }

  /**
   * Register middleware for payload transformation.
   * Middlewares are applied in registration order.
   */
  useMiddleware(fn: (event: string, payload: unknown) => unknown): void {
    this.middlewares.push(fn);
  }

  /**
   * Register interceptor for event filtering/cancellation.
   * If an interceptor returns false, the event is cancelled.
   */
  useInterceptor(fn: (event: string, payload: unknown) => boolean | void): void {
    this.interceptors.push(fn);
  }

  /**
   * Emit an event with a payload.
   * Automatically forwards to transports unless localOnly is set.
   */
  emit<K extends keyof Events & string>(event: K, payload: Events[K]): void {
    this.emitRaw(event, payload, { localOnly: false });
  }

  /**
   * Internal method for emitting events.
   * Handles middleware, interceptors, hierarchical matching, and transport forwarding.
   */
  private emitRaw(event: string, payload: unknown, opts: { localOnly: boolean }): void {
    // CRITICAL: Prevent infinite recursion from event loops
    if (this.emitDepth > this.maxEmitDepth) {
      const error = new Error(`EventBus: Max emit depth exceeded (${this.maxEmitDepth}) - possible infinite loop`);
      this.emitDepth = 0; // Reset to prevent cascade
      return;
    }

    this.emitDepth++;
    try {
      // Interceptors can cancel (e.g., prevent duplicate floods)
      for (const ic of this.interceptors) {
        const res = ic(event, payload);
        if (res === false) return;
      }

      // Middlewares can transform payloads
      let transformedPayload = payload;
      for (const mw of this.middlewares) {
        transformedPayload = mw(event, transformedPayload);
      }

      // Direct listeners
      this.listeners.get(event)?.forEach((h) => h(transformedPayload));

      // Hierarchical bubbling: "a.b.c" → "a.b.*" → "a.*"
      const parts = event.split('.');
      while (parts.length > 1) {
        parts.pop();
        const parent = parts.join('.') + '.*';
        this.listeners.get(parent)?.forEach((h) => h(transformedPayload));
      }

      // Forward to transports (unless local-only)
      if (!opts.localOnly) {
        for (const t of this.transports) {
          t.send(event, transformedPayload);
        }
      }

      // Log event (debug level for all events)
    } finally {
      this.emitDepth--;
    }
  }

  /**
   * Clear all event listeners.
   * Useful for cleanup or testing.
   */
  clearAll(): void {
    this.listeners.clear();
  }
}

/**
 * Global EventBus instance.
 * This is the central communication hub for all Pivot.is modules.
 */
export const bus = new EventBus<PivotEventMap>();

